let observerInstance = null
let mutationObserverInstance = null

export default function animation() {
  const config = {
    threshold: 0.1,
    staggerDelay: 100,
    staggerDelaySlow: 250,
  }

  // Set to track elements' order since IntersectionObserver doesn't fire sequentially
  const elementOrder = new Set()

  // Map to track the number of elements per container to calculate the delay
  const elementsPerContainer = new Map()

  // Set up IntersectionObserver
  const observer = new IntersectionObserver(handleIntersection, { threshold: config.threshold })

  /**
   * Handle elements when they intersect with the viewport
   * @param {IntersectionObserverEntry[]} entries
   */
  function handleIntersection(entries) {
    // Get the intersecting elements and sort them based on their order in the DOM
    const intersectingEntries = entries
      .filter(entry => entry.isIntersecting)
      .sort((a, b) => {
        return Array.from(elementOrder).indexOf(a.target) - Array.from(elementOrder).indexOf(b.target)
      })

    intersectingEntries.forEach(entry => {
      const element = entry.target

      const container =
        element.closest('.stagger-container') || element.closest('section') || element.closest('nav') || document.body

      if (!elementsPerContainer.has(container)) {
        elementsPerContainer.set(container, 0)
      }

      const numberOfElements = elementsPerContainer.get(container)
      const previousVisibleElements = container.querySelectorAll('.is-visible').length

      const delay = element.classList.contains('is-stagger-slow')
        ? `${(numberOfElements - previousVisibleElements) * config.staggerDelaySlow}ms`
        : element.classList.contains('is-stagger')
          ? `${(numberOfElements - previousVisibleElements) * config.staggerDelay}ms`
          : '0ms'

      requestAnimationFrame(() => {
        element.style.transitionDelay = delay
        element.classList.add('is-visible')

        // Reset transition delay after animation completes to prevent delay on subsequent animations
        element.addEventListener(
          'transitionend',
          () => {
            element.style.transitionDelay = '0ms'
          },
          { once: true }
        )
      })

      elementsPerContainer.set(container, numberOfElements + 1)
      observer.unobserve(element)
    })
  }

  function observeNewElements(elements) {
    elements.forEach(element => {
      if (!elementOrder.has(element)) {
        elementOrder.add(element)
      }
      observer.observe(element)
    })
  }

  // Set up MutationObserver for new items added to the DOM
  const mutationObserver = new MutationObserver(mutations => {
    const newElements = []

    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && node.matches('.animate')) {
          newElements.push(node)
        } else if (node.nodeType === 1) {
          newElements.push(...node.querySelectorAll('.animate'))
        }
      })
    })

    if (newElements.length) {
      observeNewElements(newElements)
    }
  })

  // Initial setup
  const initialElements = document.querySelectorAll('.animate')
  observeNewElements(initialElements)

  observerInstance = observer
  mutationObserverInstance = mutationObserver

  mutationObserver.observe(document.body, { childList: true, subtree: true })
}

export function cleanup() {
  if (observerInstance) {
    observerInstance.disconnect()
  }
  if (mutationObserverInstance) {
    mutationObserverInstance.disconnect()
  }
  observerInstance = null
  mutationObserverInstance = null
}
